import type { ZonedTimeString } from "@poi-planner/shared";
import {
  addDaysToDate,
  enumerateDates,
  epochMs,
  zonedFromDateAndTime,
  zonedTimeOfDay,
  zonedTimeOfDayInZone,
} from "./zonedTime";

export type RangeEdge = "start" | "end";

export interface DateRange {
  startDate: string;
  endDate: string;
}

/** Move an inclusive date range while keeping the grabbed date under the cursor. */
export function moveDateRange(
  startDate: string,
  endDate: string,
  targetDate: string,
  grabbedIndex: number,
  minimumDate: string,
  maximumDate: string,
): DateRange | null {
  const dateCount = enumerateDates(startDate, endDate).length;
  if (grabbedIndex < 0 || grabbedIndex >= dateCount) return null;
  const nextStartDate = addDaysToDate(targetDate, -grabbedIndex);
  const nextEndDate = addDaysToDate(nextStartDate, dateCount - 1);
  if (nextStartDate < minimumDate || nextEndDate > maximumDate) return null;
  return { startDate: nextStartDate, endDate: nextEndDate };
}

/** Resize one edge of an inclusive date range without allowing it to invert. */
export function resizeDateRange(
  startDate: string,
  endDate: string,
  edge: RangeEdge,
  targetDate: string,
  minimumDate: string,
  maximumDate: string,
): DateRange | null {
  const nextStartDate = edge === "start" ? targetDate : startDate;
  const nextEndDate = edge === "end" ? targetDate : endDate;
  if (
    nextStartDate < minimumDate ||
    nextEndDate > maximumDate ||
    nextStartDate > nextEndDate
  ) {
    return null;
  }
  return { startDate: nextStartDate, endDate: nextEndDate };
}

export function moveIntervalEdgeToDate(
  startTime: ZonedTimeString,
  endTime: ZonedTimeString,
  edge: RangeEdge,
  date: string,
  zone: string,
  sourceDisplayZone?: string,
): { startTime: ZonedTimeString; endTime: ZonedTimeString } | null {
  const moved = zonedFromDateAndTime(
    date,
    sourceDisplayZone
      ? zonedTimeOfDayInZone(
          edge === "start" ? startTime : endTime,
          sourceDisplayZone,
        )
      : zonedTimeOfDay(edge === "start" ? startTime : endTime),
    zone,
  );
  const next = {
    startTime: edge === "start" ? moved : startTime,
    endTime: edge === "end" ? moved : endTime,
  };
  return epochMs(next.endTime) > epochMs(next.startTime) ? next : null;
}

export interface AccommodationRangeChange {
  updates: Record<string, string | null>;
  conflicts: string[];
}

export interface AccommodationMove extends AccommodationRangeChange {
  start: string;
  end: string;
}

export function buildAccommodationRangeChange(
  sleep: Record<string, string>,
  poiId: string,
  oldStart: string,
  oldEnd: string,
  newStart: string,
  newEnd: string,
): AccommodationRangeChange | null {
  if (newStart > newEnd) return null;
  const oldDates = new Set(enumerateDates(oldStart, oldEnd));
  const newDates = new Set(enumerateDates(newStart, newEnd));
  const updates: Record<string, string | null> = {};
  const conflicts: string[] = [];

  for (const date of oldDates) {
    if (!newDates.has(date) && sleep[date] === poiId) updates[date] = null;
  }
  for (const date of newDates) {
    const existing = sleep[date];
    if (existing && existing !== poiId) conflicts.push(date);
    if (existing !== poiId) updates[date] = poiId;
  }
  return { updates, conflicts };
}

/** Move a consecutive nightly stay, keeping the grabbed night under the cursor. */
export function moveAccommodationStay(
  sleep: Record<string, string>,
  poiId: string,
  oldStart: string,
  oldEnd: string,
  targetDate: string,
  grabbedIndex: number,
  tripStart: string,
  tripEnd: string,
): AccommodationMove | null {
  const nightCount = enumerateDates(oldStart, oldEnd).length;
  if (grabbedIndex < 0 || grabbedIndex >= nightCount) return null;
  const start = addDaysToDate(targetDate, -grabbedIndex);
  const end = addDaysToDate(start, nightCount - 1);
  if (start < tripStart || end > tripEnd) return null;
  const change = buildAccommodationRangeChange(
    sleep,
    poiId,
    oldStart,
    oldEnd,
    start,
    end,
  );
  return change ? { ...change, start, end } : null;
}

export function stayRangeFromSegment(
  date: string,
  index: number,
  count: number,
): { start: string; end: string } {
  const start = addDaysToDate(date, -index);
  return { start, end: addDaysToDate(start, count - 1) };
}
